import Swal from 'sweetalert2';
import { supabase } from '@/lib/supabaseClient';
import { useState } from 'react';
import type { TenantInfo, TenantUser } from '../page';

interface Props {
  tenant: TenantInfo;
  users: TenantUser[];
}

const SW = { background: '#ffffff', color: '#0f172a', confirmButtonColor: '#2563eb' };

const ROLE_STYLES: Record<string, { bg: string; text: string }> = {
  owner:   { bg: 'bg-violet-50',  text: 'text-violet-700' },
  admin:   { bg: 'bg-blue-50',    text: 'text-blue-700' },
  editor:  { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  viewer:  { bg: 'bg-slate-100',  text: 'text-slate-500' },
};

export default function UsersTab({ tenant, users: initialUsers }: Props) {
  const [users, setUsers] = useState<TenantUser[]>(initialUsers);

  const handleChangeRole = async (user: TenantUser) => {
    const opts: Record<string, string> = {
      owner: 'Owner', admin: 'Admin', editor: 'Editor', viewer: 'Viewer',
    };
    const { value: newRole } = await Swal.fire({
      title: `Rol — ${user.user_id.slice(0, 8)}…`, input: 'select', inputOptions: opts,
      inputValue: user.role, showCancelButton: true,
      confirmButtonText: 'Cambiar rol', cancelButtonText: 'Cancelar', ...SW,
    });
    if (!newRole || newRole === user.role) return;
    const { error } = await supabase.from('saas_tenant_users')
      .update({ role: newRole }).eq('id', user.id);
    if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
    setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, role: newRole } : u));
    Swal.fire({ title: 'Rol actualizado', icon: 'success', timer: 1200, showConfirmButton: false, ...SW });
  };

  const handleRemoveUser = async (user: TenantUser) => {
    const { isConfirmed } = await Swal.fire({
      title: '¿Eliminar usuario?',
      html: `<p style="color:#64748b;font-size:13px">Se removerá al usuario <code>${user.user_id.slice(0, 16)}…</code> de este tenant.</p>`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Sí, eliminar', cancelButtonText: 'Cancelar',
      ...SW, confirmButtonColor: '#ef4444',
    });
    if (!isConfirmed) return;
    const { error } = await supabase.from('saas_tenant_users').delete().eq('id', user.id);
    if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
    setUsers((prev) => prev.filter((u) => u.id !== user.id));
    Swal.fire({ title: 'Usuario eliminado del tenant', icon: 'success', timer: 1200, showConfirmButton: false, ...SW });
  };

  const ownerCount  = users.filter((u) => u.role === 'owner').length;
  const adminCount  = users.filter((u) => u.role === 'admin').length;
  const remaining   = tenant.max_users >= 999 ? '∞' : String(tenant.max_users - users.length);

  return (
    <div className="space-y-5 max-w-2xl">

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total usuarios',   value: users.length,  icon: 'ri-team-line',          color: 'text-blue-600 bg-blue-50' },
          { label: 'Dueños / Admins',  value: ownerCount + adminCount, icon: 'ri-shield-user-line', color: 'text-violet-600 bg-violet-50' },
          { label: 'Slots disponibles', value: remaining,   icon: 'ri-user-add-line',       color: 'text-emerald-600 bg-emerald-50' },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
            <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${s.color} shrink-0`}>
              <i className={`${s.icon} text-base`}></i>
            </span>
            <div>
              <p className="text-slate-900 text-xl font-bold leading-none">{s.value}</p>
              <p className="text-slate-400 text-xs mt-1">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Users table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50">
          <p className="text-slate-400 text-xs uppercase tracking-wider font-medium">Usuarios del tenant</p>
          <p className="text-slate-400 text-xs">
            {users.length} / {tenant.max_users >= 999 ? '∞' : tenant.max_users} usuarios
          </p>
        </div>

        {users.length === 0 ? (
          <div className="py-14 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-slate-100 text-slate-300 mx-auto mb-3">
              <i className="ri-user-line text-2xl"></i>
            </div>
            <p className="text-slate-400 text-sm">Sin usuarios asociados a este tenant</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {users.map((user) => {
              const rs = ROLE_STYLES[user.role] ?? ROLE_STYLES.viewer;
              return (
                <div key={user.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 transition-colors">
                  {/* Avatar */}
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-br from-slate-100 to-slate-200 text-slate-500 text-sm font-bold shrink-0">
                    <i className="ri-user-line text-base"></i>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-700 text-sm font-mono truncate">
                      {user.user_id}
                    </p>
                    <p className="text-slate-400 text-xs mt-0.5">
                      Agregado: {new Date(user.created_at).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>

                  {/* Role badge — clickable */}
                  <button
                    onClick={() => handleChangeRole(user)}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize cursor-pointer hover:opacity-75 transition-opacity whitespace-nowrap ${rs.bg} ${rs.text}`}
                    title="Clic para cambiar rol"
                  >
                    {user.role}
                  </button>

                  {/* Remove */}
                  <button
                    onClick={() => handleRemoveUser(user)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                    title="Eliminar usuario del tenant"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Max users bar */}
        {tenant.max_users < 999 && (
          <div className="px-5 py-3 border-t border-slate-100 bg-slate-50">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="text-slate-400">Uso de slots de usuario</span>
              <span className="text-slate-500 font-medium">{users.length}/{tenant.max_users}</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full transition-all ${
                  users.length >= tenant.max_users ? 'bg-red-400' :
                  users.length >= tenant.max_users * 0.8 ? 'bg-amber-400' : 'bg-blue-400'
                }`}
                style={{ width: `${Math.min(100, (users.length / tenant.max_users) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Plan note */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-start gap-2.5">
        <span className="w-5 h-5 flex items-center justify-center text-slate-400 shrink-0 mt-0.5">
          <i className="ri-information-line text-sm"></i>
        </span>
        <div>
          <p className="text-slate-500 text-xs leading-relaxed">
            El plan <strong className="text-slate-700 uppercase">{tenant.plan_code}</strong> permite un máximo de{' '}
            <strong className="text-slate-700">{tenant.max_users >= 999 ? 'usuarios ilimitados' : `${tenant.max_users} usuario${tenant.max_users !== 1 ? 's' : ''}`}</strong>.
            Para modificar el límite, cambia el plan del tenant o edita los límites directamente desde Overview.
          </p>
        </div>
      </div>
    </div>
  );
}
